import { kwh } from "../../lib/format";
import { MicroLabel } from "../ui/Text";

/**
 * Consumption against quota.
 *
 * ## Why a single bar and not a chart
 *
 * The question this answers is "how close is this card to its limit, and will the
 * open session push it over". That is one ratio and one projection. A line chart
 * over the period would be a bigger object answering a question nobody asked, and
 * the session-level history it would need does not exist in this build.
 *
 * ## The two segments are visually distinct on purpose
 *
 * Committed energy is solid; in-flight energy is hatched. Spec 6.3 keeps them
 * separate because one is billable and one is a live estimate — rendering them as
 * one continuous fill would silently promote the estimate to a fact. The hatch
 * survives greyscale printing and colour-blindness, which a lighter tint of the
 * same hue would not.
 *
 * ## Thresholds are drawn where they actually fire
 *
 * The policy's warn thresholds (spec 6.3) appear as ticks. A bar that shows 87%
 * without showing that the alert fires at 80 tells the operator less than it
 * could.
 */
export function QuotaBar({
  committedWh,
  inFlightWh,
  quotaWh,
  thresholds,
}: {
  readonly committedWh: number;
  readonly inFlightWh: number;
  readonly quotaWh: number | null;
  readonly thresholds: readonly number[];
}) {
  /**
   * With no quota there is no ratio to draw. Returning the figures as text rather
   * than a full-width or zero-width bar: either would imply a limit that does not
   * exist.
   */
  if (quotaWh === null || quotaWh === 0) {
    return (
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <MicroLabel>No quota</MicroLabel>
        <span className="text-data text-ink">{kwh(committedWh)} kWh committed</span>
        {inFlightWh > 0 ? (
          <span className="text-data text-ink-faint">{kwh(inFlightWh)} kWh in flight</span>
        ) : null}
      </div>
    );
  }

  const committedPct = (committedWh / quotaWh) * 100;
  const inFlightPct = (inFlightWh / quotaWh) * 100;

  /**
   * The bar is scaled to whichever is larger: the quota, or what has actually been
   * consumed. Clamping the fill at 100% would make 340% of quota look identical to
   * 101%, which is the one case where the exact overage matters most.
   */
  const scaleMax = Math.max(100, committedPct + inFlightPct);
  const width = (value: number) => `${(value / scaleMax) * 100}%`;

  const isOver = committedPct >= 100;
  const willGoOver = !isOver && committedPct + inFlightPct >= 100;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
          <span className="text-data text-ink">
            <span className="font-semibold">{kwh(committedWh)}</span> of {kwh(quotaWh)} kWh
            committed
          </span>
          {inFlightWh > 0 ? (
            <span className="text-data text-ink-faint">+{kwh(inFlightWh)} kWh in flight</span>
          ) : null}
        </div>
        {isOver ? (
          <MicroLabel className="text-signal">Over quota</MicroLabel>
        ) : willGoOver ? (
          <MicroLabel className="text-state-transitional">
            The open session will cross the quota
          </MicroLabel>
        ) : null}
      </div>

      {/* A real progressbar role, so the ratio is available without sight. The
          text above carries the same information, which is why the track itself
          can be `aria-hidden` below. */}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(committedPct)}
        aria-valuetext={`${Math.round(committedPct)} percent of quota committed`}
        className="relative h-6 w-full border border-rule bg-paper-sunken"
      >
        <div className="absolute inset-0 flex" aria-hidden="true">
          <span
            className={`h-full ${isOver ? "bg-signal" : "bg-ink"}`}
            style={{ width: width(committedPct) }}
          />
          {inFlightPct > 0 ? (
            <span
              className="h-full"
              style={{
                width: width(inFlightPct),
                // Hatched rather than tinted: distinguishable in greyscale and to a
                // colour-blind reader, which a second hue is not.
                backgroundImage:
                  "repeating-linear-gradient(135deg, var(--color-ink-faint) 0 2px, transparent 2px 5px)",
              }}
            />
          ) : null}
        </div>

        {/* The quota line. Only drawn when the bar extends past it — otherwise it
            would sit exactly on the right edge and read as a border. */}
        {scaleMax > 100 ? (
          <span
            aria-hidden="true"
            className="absolute inset-y-0 w-0.5 bg-rule-strong"
            style={{ left: width(100) }}
          />
        ) : null}

        {thresholds
          // A threshold at or beyond the drawn range has nowhere to sit.
          .filter((threshold) => threshold < scaleMax)
          .map((threshold) => (
            <span
              key={threshold}
              aria-hidden="true"
              className="absolute -bottom-1 top-auto h-2 w-px bg-ink-faint"
              style={{ left: width(threshold) }}
            />
          ))}
      </div>

      {thresholds.length === 0 ? null : (
        <MicroLabel>
          Warns at {thresholds.map((threshold) => `${threshold}%`).join(" · ")}
        </MicroLabel>
      )}
    </div>
  );
}
